// Tenant Entitlements Service
// ----------------------------
// Per-slot ("app license") accessor. Quota is per web app, not tenant-wide, so
// this — not getTenantUsage — is the source of truth for every per-app pentest
// cue (Applications rows, the Pentest-Now gate) and the Billing license list.
//
// Calls the BE listTenantEntitlements query, tenant-scoped server-side by the
// caller's Cognito tenantId (the FE never passes a tenant id). Failures
// propagate so the hook can surface an error rather than a misleading budget.

import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";
import { listTenantEntitlements } from "@/services/graphql/queries";
import { CacheKey } from "@/constants/definitions";
import { setCache } from "@/utils/cache";
import { getGraphData } from "@/utils/transform";

// Plan type + status string unions mirror the BE (models/definitions.py).
export type EntitlementPlanType = "one_off" | "subscription";
export type EntitlementStatus =
  | "unbound"
  | "bound"
  | "consumed_locked"
  | "inactive";

export interface TenantEntitlement {
  id: string;
  planType: EntitlementPlanType;
  planId: string;
  status: EntitlementStatus;
  // The web app this license is attached to, or null while unbound (bought,
  // not yet assigned to an app).
  boundWebAppId: string | null;
  testsUsedThisPeriod: number;
  testsAllowed: number;
  // One-off only: epoch seconds when this app's access window closes. Null for
  // subscription slots (they roll, they don't expire).
  accessExpiresAt: number | null;
  // Subscription only: epoch seconds when this slot's pentest counter next
  // resets — the subscription's OWN monthly anniversary (two apps subscribed on
  // different days reset on different days), and monthly even when the plan is
  // billed annually. Null for one-off slots. Derived server-side from the same
  // anchor the start gate enforces, so the date shown can't drift from the
  // boundary applied.
  periodEndsAt: number | null;
  createdAt: number | null;
}

// Force Cognito auth + reject any client-side tenantId leak, symmetric to the
// BE rule "never accept tenantId from client input".
const graphqlOperation = (query: any, variables?: any) => {
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

export async function fetchTenantEntitlements(): Promise<TenantEntitlement[]> {
  const response = await API.graphql(graphqlOperation(listTenantEntitlements));
  const rows = getGraphData(response as any, "listTenantEntitlements");
  // getGraphData returns false on a missing/errored payload; an empty account
  // legitimately has no licenses, so treat a falsy payload as "none".
  return Array.isArray(rows) ? (rows as TenantEntitlement[]) : [];
}

// Warm the license cache without rendering anything.
//
// Called from the login bootstrap once the dashboard's own preloads have
// settled. License state isn't needed to render the dashboard, but it IS the
// first thing the Add-App page blocks on, so fetching it during the idle
// window after login turns that page's visible wait into a cache read.
//
// Errors are swallowed by design: this is a speculative warm-up, and every
// consumer still fetches for itself through useTenantEntitlements. A failure
// here costs a slower Add-App page, nothing more.
export async function warmTenantEntitlementsCache(): Promise<void> {
  try {
    const rows = await fetchTenantEntitlements();
    await setCache(CacheKey.listTenantEntitlements, rows);
  } catch {
    // Non-fatal — see above.
  }
}

// Why a bound license blocks a new pentest — mirrors the BE quota reason codes
// so the FE prompt and the server's rejection read the same. null = allowed.
export type EntitlementBlockReason =
  | "at_limit"
  | "expired"
  | "inactive"
  | null;

export interface EntitlementView extends TenantEntitlement {
  testsRemaining: number;
  isAtLimit: boolean;
  // One-off window has closed (accessExpiresAt in the past).
  isExpired: boolean;
  isInactive: boolean;
  blockReason: EntitlementBlockReason;
}

export function deriveEntitlementView(
  entitlement: TenantEntitlement,
  nowEpochSeconds: number,
): EntitlementView {
  const testsRemaining = Math.max(
    0,
    entitlement.testsAllowed - entitlement.testsUsedThisPeriod,
  );
  const isAtLimit = entitlement.testsAllowed > 0 && testsRemaining === 0;
  const isExpired =
    entitlement.accessExpiresAt != null &&
    nowEpochSeconds > entitlement.accessExpiresAt;
  const isInactive = entitlement.status === "inactive";

  // Precedence matches the BE start gate: inactive → expired → at-limit.
  const blockReason: EntitlementBlockReason = isInactive
    ? "inactive"
    : isExpired
      ? "expired"
      : isAtLimit
        ? "at_limit"
        : null;

  return {
    ...entitlement,
    testsRemaining,
    isAtLimit,
    isExpired,
    isInactive,
    blockReason,
  };
}

// The date qualifier that follows a remaining-count, e.g. the "· resets Aug 20,
// 2026" in "3 of 5 pentests left · resets Aug 20, 2026". Empty when there is no
// date to show, so callers can append it unconditionally.
//
// Shared by every surface that prints a count (Applications rows, the Pentest
// button's hover cue, the blocked banner) so they can't describe the same
// license two different ways. Which date applies is a property of the plan:
//
//   * one-off      -> when access ENDS (the allowance never refills)
//   * subscription -> when the counter RESETS (its own monthly anniversary)
//
// A subscription with no periodEndsAt (BE could not derive an anchor) yields
// "", which is the pre-existing vaguer behavior rather than a wrong date.
export function entitlementDateQualifier(
  entitlement: EntitlementView,
  formatDate: (epochSeconds: number) => string,
): string {
  if (entitlement.planType === "one_off") {
    return entitlement.accessExpiresAt
      ? ` · access ends ${formatDate(entitlement.accessExpiresAt)}`
      : "";
  }
  return entitlement.periodEndsAt
    ? ` · resets ${formatDate(entitlement.periodEndsAt)}`
    : "";
}

// Human copy for a block reason — kept in sync with quota.reason_to_message on
// the BE, but phrased for an inline FE prompt.
export function blockReasonMessage(reason: EntitlementBlockReason): string {
  switch (reason) {
    case "at_limit":
      return "No pentests remaining for this app this period.";
    case "expired":
      return "This app's pentest access window has ended.";
    case "inactive":
      return "This app's license is inactive. Existing results remain available.";
    default:
      return "";
  }
}
